import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { CartContext } from '@rsc/contexts/CartContext';
import { CustomerContext } from '@rsc/contexts/CustomerContext';
import { LocaleContext } from '@rsc/contexts/LocaleContext';
import { TrackClickAfterSearch } from '@rsc/utils/algoliaAnalytics';
import getInternalLink from '@rsc/utils/getInternalLink';
import getPercentageDiscountPrice from '@rsc/utils/getPercentageDiscountPrice';
import LinkComponent from '../../LinkComponent';
import styles from '../../styles';

const Container = styled.div`
  flex: 1;
  border-bottom: solid 1px
    ${({ theme }) => theme.colors.searchFormHitBorderColor};

  &::first-child {
    border-top: none;
  }

  a {
    color: ${({ theme }) => theme.colors.searchInput};
    display: flex;
    padding: ${styles.spacing['spacing-sm']} ${styles.spacing['spacing-md']};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  p {
    font-size: ${styles.fontSize.sm};
    margin: 0;
    font-weight: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  h3,
  h4 {
    font-size: inherit;
    font-weight: normal;
    margin: 0;
  }

  h3 {
    margin: 0 0 ${styles.spacing['spacing-2xs']};
    color: inherit;
    font-size: ${styles.fontSize.base};
  }

  h4 {
    font-size: ${styles.fontSize.sm};
  }
`;

const ImageContainer = styled.div`
  width: ${styles.spacing['spacing-2xl']};
  height: ${styles.spacing['spacing-2xl']};
  margin-right: ${styles.spacing['spacing-sm']};
  flex-shrink: 0;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

function SearchFormHit({ hit, insights }) {
  const { title, excerpt, image, slug, link, variants } = hit;
  const { formatPrice } = useContext(CartContext);
  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency || 'USD';
  const context = useContext(CustomerContext);
  const { isProStaff } = context;

  const isProduct = variants?.length > 0;
  const linkUrl = isProduct ? `/${slug}` : link;

  const getCadDisplayPrice = () => (
    <>
      {isProStaff() && hit?.cadCompareAtPrice && (
        <h4>{formatPrice(hit.cadCompareAtPrice)}</h4>
      )}
      {isProStaff() && !hit?.cadCompareAtPrice && (
        <h4>
          <strike>{formatPrice(hit.cadPrice)}</strike>{' '}
          {`${formatPrice(
            getPercentageDiscountPrice({
              price: hit.cadPrice,
              percentageDiscount:
                process.env.GATSBY_PRO_STAFF_DISCOUNT_PERCENTAGE,
            })
          )}`}
        </h4>
      )}
      {!isProStaff() && <h4>{formatPrice(hit.cadPrice)}</h4>}
    </>
  );

  const getDisplayPrice = () => (
    <>
      {isProStaff() && variants[0]?.compareAtPrice?.amount && (
        <h4>{formatPrice(variants[0]?.price)}</h4>
      )}
      {isProStaff() && !variants[0]?.compareAtPrice && (
        <h4>
          <strike>{formatPrice(variants[0]?.price)}</strike>{' '}
          {`${formatPrice(
            getPercentageDiscountPrice({
              price: variants[0]?.price,
              percentageDiscount:
                process.env.GATSBY_PRO_STAFF_DISCOUNT_PERCENTAGE,
            })
          )}`}
        </h4>
      )}
      {!isProStaff() && <h4>{formatPrice(variants[0]?.price)}</h4>}
    </>
  );

  // Hide product cards that don't have price
  if (isProduct && currency === 'CAD' && !hit.cadPrice) {
    return null;
  }

  return (
    <Container>
      <LinkComponent
        to={getInternalLink({ slug: linkUrl, currency })}
        onClick={() => {
          TrackClickAfterSearch(insights);
        }}
        // eslint-disable-next-line no-underscore-dangle
        state={{ searchedProduct: hit?.__queryID || null }}
        tabIndex={-1} // Don't focus on TAB since we use arrow keys to navigate inside the result box
      >
        {image && (
          <ImageContainer>
            <Image src={`${image}${isProduct ? '&width=68' : ''}`} />
          </ImageContainer>
        )}
        <div>
          <h3>{title}</h3>
          {isProduct && currency === 'CAD' && getCadDisplayPrice()}
          {isProduct && currency !== 'CAD' && getDisplayPrice()}
          {!isProduct && <p>{excerpt}</p>}
        </div>
      </LinkComponent>
    </Container>
  );
}

SearchFormHit.propTypes = {
  hit: PropTypes.shape({
    title: PropTypes.string.isRequired,
    excerpt: PropTypes.string,
    image: PropTypes.string.isRequired,
    slug: PropTypes.string,
    link: PropTypes.string,
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        price: PropTypes.number,
        compareAtPrice: PropTypes.string,
      })
    ),
    __queryID: PropTypes.string,
    cadPrice: PropTypes.number,
    cadCompareAtPrice: PropTypes.number,
  }),
  insights: PropTypes.func.isRequired,
};

SearchFormHit.defaultProps = {
  hit: {
    excerpt: '',
    variants: [],
    slug: '',
    link: '',
  },
};

export default SearchFormHit;
